import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Event as EventIcon, Person as PersonIcon } from "@mui/icons-material";
import { isLessonAlreadySignedUp } from "../SignUpForLessonForm";
import StoreModal from "../../StoreModal";
import LoginModal from "../../Login";
import { useAuthStore } from "../../../store";
import "../../../App.css";

// Import utilities
import { MONTHS_PL, AcceptRegulaminLabel, payDialogOn } from "./utils/constants";
import { formatDateLine } from "./utils/formatters";

// Import dialogs
import AlreadySignedUpDialog from "./dialogs/AlreadySignedUpDialog";
import SimpleConfirmDialog from "./dialogs/SimpleConfirmDialog";
import PaymentChoiceDialog from "./dialogs/PaymentChoiceDialog";
import UnpaidDebtDialog from "./dialogs/UnpaidDebtDialog";

// Import components
import LessonLocation from "./components/LessonLocation";
import PassSelector from "./components/PassSelector";
import PaymentSelector from "./components/PaymentSelector";
import StatuteHighlights from "./components/StatuteHighlights";

// Import hooks
import { useLessonData } from "./hooks/useLessonData";
import { useSignUpLogic } from "./hooks/useSignUpLogic";

const SignUpForLessonModal = ({ open, onClose, setNotification, lessonData }) => {
  // Auth store
  const {
    isAuthenticated,
    addLesson,
    email,
    name,
    lastName,
    phone,
    user,
    fetchLessons,
    fetchPendingSignups,
    markPayOnSite,
  } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    addLesson: state.addLesson,
    email: state.email,
    name: state.name,
    lastName: state.lastName,
    phone: state.phone,
    user: state.user,
    fetchLessons: state.fetchLessons,
    fetchPendingSignups: state.fetchPendingSignups,
    markPayOnSite: state.markPayOnSite,
  }));

  // Local state
  const [error, setError] = useState("");
  const [alreadySignedUp, setAlreadySignedUp] = useState(false);
  const [formValues, setFormValues] = useState({ signInMethod: "", selectedPass: null });
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [acceptRegulamin, setAcceptRegulamin] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [eventPasses, setEventPasses] = useState([]);
  const [eventStoreModalOpen, setEventStoreModalOpen] = useState(false);

  // Custom hooks
  const { isDataLoading, passes, canSignData, fetchCanSignData } = useLessonData(
    open,
    isAuthenticated,
    user,
    lessonData
  );

  const {
    loading,
    setLoading,
    signingOut,
    handleUserToLesson,
    handleSignOutLesson,
    handleRemoveFromWaitlist,
    handlePayment,
    handlePayDebt
  } = useSignUpLogic(
    user,
    email,
    name,
    lastName,
    phone,
    isAuthenticated,
    addLesson,
    setNotification
  );

  // Theme
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Computed values
  const header = `Zapisz się na ${lessonData.lesson_name || ""}`;
  const dateLine = formatDateLine(lessonData.lesson_start, lessonData.lesson_week_day, MONTHS_PL);
  const trainerName = lessonData.trainer?.full_name || "";

  // Effects
  useEffect(() => {
    if (!open) {
      setAlreadySignedUp(false);
      setEventPasses([]);
      return;
    }

    const init = async () => {
      setFormValues({ signInMethod: "", selectedPass: null });
      await fetchLessons();
      setAlreadySignedUp(isLessonAlreadySignedUp(lessonData.lesson_id));
      
      // Fetch available event passes if this lesson is part of an event
      if (lessonData.event_id && isAuthenticated && user) {
        try {
          const idToken = await user.getIdToken();
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/for-event/${lessonData.event_id}`,
            { headers: { Authorization: `Bearer ${idToken}` } }
          );
          const data = await response.json();
          if (Array.isArray(data.objects)) {
            setEventPasses(data.objects);
          } else {
            setEventPasses([]);
          }
        } catch (err) {
          console.error('Error fetching event passes:', err);
          setEventPasses([]);
        }
      } else {
        setEventPasses([]);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  // Flow handlers
  const handleFreeLesson = async () => {
    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData);
    setLoading(false);
    if (ok) onClose();
  };

  const handlePaymentOnSite = async () => {
    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData);
    setLoading(false);
    if (ok) onClose();
  };

  const handleSubmitPaid = async () => {
    if (formValues.signInMethod === "karnet" && !formValues.selectedPass) {
      return setError("Proszę wybrać karnet");
    }

    if (!acceptRegulamin) {
      return setError("Proszę zaakceptować Regulamin");
    }

    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData);
    setLoading(false);

    if (ok && formValues.signInMethod === "pojedyncza") {
      // For payment-only-online lessons, auto-redirect to payment
      if (lessonData?.payment_only_online) {
        await handlePayment(lessonData);
      } else {
        setConfirmationOpen(payDialogOn);
      }
    } else if (ok) {
      onClose();
    }
  };

  const handleSignOut = async () => {
    const success = await handleSignOutLesson(lessonData);
    if (success) onClose();
  };

  const handleRemoveWaitlist = async () => {
    const success = await handleRemoveFromWaitlist(lessonData, fetchCanSignData);
    if (success) onClose();
  };

  // Early returns for simple flows
  if (lessonData?.sign_up_disabled) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="span" fontWeight="600" sx={{ display: 'block' }}>
            {header}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
            {dateLine}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon="🔒" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="500">
              Zapisy na te zajęcia są wyłączone
            </Typography>
            <Typography variant="caption">
              Administrator wyłączył możliwość zapisywania się na te zajęcia. Skontaktuj się ze studiem, aby uzyskać więcej informacji.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" fullWidth={isMobile} sx={{ minHeight: 44 }}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (canSignData.has_unpaid_debt) {
    return (
      <UnpaidDebtDialog
        open={open}
        onClose={onClose}
        debtAmount={canSignData.debt_amount}
        debtCount={canSignData.debt_count}
        debtDetails={canSignData.debt_details}
        onPayDebt={() => handlePayDebt(canSignData)}
      />
    );
  }

  if (alreadySignedUp) {
    return (
      <AlreadySignedUpDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        signingOut={signingOut}
        onSignOut={handleSignOut}
        lessonData={lessonData}
      />
    );
  }

  if (lessonData?.is_free) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Te zajęcia są darmowe. Czy chcesz się zapisać?"
        loading={loading}
        onConfirm={handleFreeLesson}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  if (lessonData?.payment_on_site && !lessonData?.payment_only_online) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Płatność za te zajęcia odbywa się na miejscu. Czy chcesz się zapisać?"
        loading={loading}
        onConfirm={handlePaymentOnSite}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  // Handle payment_only_online lessons without pass
  if (lessonData?.payment_only_online && !lessonData?.is_free && !canSignData.has_pass) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Te zajęcia wymagają płatności online. Po zapisaniu się zostaniesz automatycznie przekierowany do płatności."
        loading={loading}
        onConfirm={async () => {
          setLoading(true);
          const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData);
          if (ok) {
            await handlePayment(lessonData);
          }
          setLoading(false);
        }}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  // Main paid flow dialog
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: isMobile ? 2 : 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          pt: 3,
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="span" fontWeight="600" gutterBottom sx={{ display: 'block' }}>
            {header}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
            {dateLine}
          </Typography>
          {trainerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" component="span" color="text.secondary" fontWeight="500">
                {trainerName}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        
        {/* Location */}
        {lessonData?.lesson_type?.location && (
          <Box sx={{ px: 3, pb: 2 }}>
            <LessonLocation location={lessonData.lesson_type.location} />
          </Box>
        )}

        {isDataLoading ? (
          <DialogContent>
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          </DialogContent>
        ) : (
          <>
            <DialogContent sx={{ pt: 2, pb: 3 }}>
              <Stack spacing={2.5}>
                {/* Event Pass Purchase Option - only show if user doesn't have event pass */}
                {lessonData.event_id && eventPasses.length > 0 && !canSignData.has_pass && (() => {
                  const eventColor = lessonData.event_color || '#ff9800';
                  return (
                    <Card 
                      elevation={0} 
                      sx={{ 
                        border: '2px solid',
                        borderColor: eventColor,
                        bgcolor: `${eventColor}15`,
                        borderRadius: 2
                      }}
                    >
                      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon sx={{ color: eventColor }} />
                            <Typography variant="body2" fontWeight="600">
                              🎫 Karnet na wydarzenie "{lessonData.event_name}"
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Dla tego wydarzenia dostępny jest karnet eventowy – z jednym karnetem wchodzisz na wszystkie zajęcia! 🎫
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => setEventStoreModalOpen(true)}
                            startIcon={<EventIcon />}
                            fullWidth
                            sx={{ 
                              minHeight: 44,
                              bgcolor: eventColor,
                              '&:hover': {
                                bgcolor: eventColor,
                                filter: 'brightness(0.9)',
                              }
                            }}
                          >
                            Kup karnet eventowy
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Availability and Waitlist Status */}
                {!canSignData.available && !canSignData.is_on_waitlist && !canSignData.pass_expired && (
                  <Alert severity="warning" icon="⏳" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500" gutterBottom>
                      Zajęcia są pełne
                    </Typography>
                    <Typography variant="caption">
                      Możesz dołączyć do listy oczekujących. Otrzymasz powiadomienie, jeśli pojawi się wolne miejsce.
                    </Typography>
                  </Alert>
                )}
                
                {canSignData.is_on_waitlist && (
                  <Alert severity="info" icon="📋" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500" gutterBottom>
                      Jesteś na liście oczekujących
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption">
                        Pozycja:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {canSignData.waitlist_position}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      Otrzymasz powiadomienie, jeśli pojawi się wolne miejsce.
                    </Typography>
                  </Alert>
                )}

                {/* Sign-in options */}
                {(canSignData.available || !canSignData.is_on_waitlist) && (
                  <Box>
                    {canSignData.has_pass && !canSignData.pass_expired ? (
                      <PassSelector
                        passes={passes}
                        selectedPass={formValues.selectedPass}
                        onPassChange={(passId) =>
                          setFormValues({
                            ...formValues,
                            selectedPass: passId,
                            signInMethod: "karnet",
                          })
                        }
                      />
                    ) : (
                      <PaymentSelector
                        lessonData={lessonData}
                        signInMethod={formValues.signInMethod}
                        onMethodSelect={(method) =>
                          setFormValues({ ...formValues, signInMethod: method })
                        }
                        onBuyPassClick={() => setStoreModalOpen(true)}
                        passExpired={canSignData.pass_expired}
                      />
                    )}
                  </Box>
                )}

                {error && (
                  <Alert variant="filled" severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Statute Highlights */}
                {(canSignData.available || !canSignData.is_on_waitlist) && (
                  <StatuteHighlights />
                )}

                {/* Regulamin - Mobile only */}
                {isMobile && (canSignData.available || !canSignData.is_on_waitlist) && (
                  <>
                    <Divider />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={acceptRegulamin} 
                          onChange={(e) => setAcceptRegulamin(e.target.checked)} 
                        />
                      }
                      label={AcceptRegulaminLabel}
                      sx={{ mx: 0 }}
                    />
                  </>
                )}
              </Stack>
            </DialogContent>

            <DialogActions sx={{ 
              px: 3, 
              pb: 3, 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {/* Regulamin checkbox - Desktop only */}
              {!isMobile && (canSignData.available || !canSignData.is_on_waitlist) && (
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={acceptRegulamin} 
                      onChange={(e) => setAcceptRegulamin(e.target.checked)} 
                      size="small"
                    />
                  }
                  label={AcceptRegulaminLabel}
                  sx={{ mr: 'auto' }}
                />
              )}
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                width: isMobile ? '100%' : 'auto',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Button 
                  onClick={onClose} 
                  variant="outlined"
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  Anuluj
                </Button>
              
                {canSignData.available && (
                  <Button
                    onClick={handleSubmitPaid}
                    color="success"
                    variant="contained"
                    disabled={
                      loading ||
                      !acceptRegulamin ||
                      !formValues.signInMethod ||
                      (formValues.signInMethod === "karnet" && !formValues.selectedPass)
                    }
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Zapisz się"}
                  </Button>
                )}
              
                {!canSignData.available && !canSignData.is_on_waitlist && !canSignData.pass_expired && (
                  <Button
                    onClick={handleSubmitPaid}
                    color="primary"
                    variant="contained"
                    disabled={loading || !acceptRegulamin}
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Dołącz do listy oczekujących"}
                  </Button>
                )}
              
                {canSignData.is_on_waitlist && (
                  <Button
                    onClick={handleRemoveWaitlist}
                    color="warning"
                    variant="contained"
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Usuń z listy oczekujących"}
                  </Button>
                )}
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Payment confirmation dialog */}
      <PaymentChoiceDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onPayOnline={async () => {
          setConfirmationOpen(false);
          onClose();
          await handlePayment(lessonData);
        }}
        onPayOnSite={async () => {
          setConfirmationOpen(false);
          onClose();
          setNotification({
            message: "Zapisano na zajęcia!",
            type: "success"
          });
          try {
            await markPayOnSite(lessonData.lesson_id);
            await fetchPendingSignups();
          } catch (e) {
            // Non-fatal: the signup already exists; the cash reminder simply
            // won't surface until the next owed refresh.
          }
        }}
        paymentOnlyOnline={lessonData?.payment_only_online}
        lessonData={lessonData}
      />

      {/* Store & login modals */}
      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
        lessonCategoryIds={lessonData.lesson_category_ids}
        lessonId={lessonData.lesson_id}
      />
      {/* Event-specific Store Modal */}
      <StoreModal
        open={eventStoreModalOpen}
        onClose={() => setEventStoreModalOpen(false)}
        eventId={lessonData.event_id}
        lessonId={lessonData.lesson_id}
        eventColor={lessonData.event_color}
      />
      <LoginModal open={isLoginOpen} handleClose={() => setIsLoginOpen(false)} />
    </>
  );
};

export default SignUpForLessonModal;
export { payDialogOn } from "./utils/constants";
